import * as Accordion from '@radix-ui/react-accordion'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { GripVertical, Trash2 } from 'lucide-react'
import { TurnType } from '@db8/shared'
import { useWatch } from 'react-hook-form'
import { CSS } from '@dnd-kit/utilities'
import { useSortable } from '@dnd-kit/sortable'
import { useEffect, useRef } from 'react'

export const CreateStage = ({ form, field, index, remove }) => {
  const stage =
    useWatch({
      control: form.control,
      name: `stages.${index}`,
    }) || {}

  const abbreviation =
    useWatch({
      control: form.control,
      name: `stages.${index}.abbreviation`,
    }) || ''
  const prevTypeRef = useRef(stage.type)

  useEffect(() => {
    const prevType = prevTypeRef.current
    const currentType = stage.type
    const abbreviation = stage.abbreviation || ''

    const getDefaultAbbreviation = (type: TurnType) => {
      // @ts-ignore
      if (type === TurnType.Affirmative) return `${countType('Affirmative')}A`
      // @ts-ignore
      if (type === TurnType.Negative) return `${countType('Negative')}N`
      if (type === TurnType.Preperatory) return `PREP`

      if (type === TurnType.AffirmativeCrossNegative)
        // @ts-ignore
        return `${countType('AffirmativeCrossNegative')}(AxN)`

      if (type === TurnType.NegativeCrossAffirmative)
        // @ts-ignore
        return `${countType('NegativeCrossAffirmative')}(NxA)`
      return ''
    }

    const countType = (targetType: TurnType) => {
      const allStages = form.getValues('stages') || []
      const stagesBefore = allStages.slice(0, index)
      return stagesBefore.filter((stage) => stage.type === targetType).length + 1
    }

    if (currentType !== prevType) {
      const expectedOldAbbreviation = getDefaultAbbreviation(prevType)
      const expectedNewAbbreviation = getDefaultAbbreviation(currentType)

      if (abbreviation === expectedOldAbbreviation || abbreviation === '') {
        form.setValue(`stages.${index}.abbreviation`, expectedNewAbbreviation)
      }
      prevTypeRef.current = currentType
    }
  }, [stage.type])

  let emoji = ''
  if (stage.type === TurnType.Affirmative) emoji = '🟢'
  else if (stage.type === TurnType.Negative) emoji = '🔵'
  else if (stage.type === TurnType.AffirmativeCrossNegative) emoji = '🟢 x 🔵'
  else if (stage.type === TurnType.NegativeCrossAffirmative) emoji = '🔵 x 🟢'
  else if (stage.type === TurnType.Preperatory) emoji = '⌛'

  return (
    <Accordion.Item
      value={field.id}
      className="relative border p-2 md:p-3 rounded-md flex flex-col bg-muted"
    >
      <Accordion.Trigger className="cursor-pointer w-full text-left font-semibold">
        <Accordion.Header>
          <div className="flex items-start sm:items-center space-x-2">
            <GripVertical className="cursor-grab flex-shrink-0 mt-1 sm:mt-0" />
            <div className="flex flex-col flex-grow min-w-0">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="truncate font-medium text-base">
                  {stage.title || 'Unnamed Stage'}
                </span>

                <Button
                  variant="ghost"
                  onClick={() => remove(index)}
                  title="Remove Stage"
                  className="rounded-full p-1 dark:text-white hover:text-red-600 bg-gray-200 hover:bg-gray-100 dark:bg-background w-[30px] h-[30px]"
                >
                  <Trash2 size={15} />
                </Button>
              </div>

              <div className="flex justify-between items-center gap-2 text-xs font-semibold">
                <span>
                  <span className="font-bold">{abbreviation}</span> {stage.type || ''} {emoji}
                </span>
                <span className="whitespace-nowrap">
                  {stage.duration || 0} min{stage.duration > 1 && 's'}
                </span>
              </div>
            </div>
          </div>
        </Accordion.Header>
      </Accordion.Trigger>

      <Accordion.Content className="pt-3">
        <div className="space-y-4">
          <div className="flex w-full space-x-2">
            <FormField
              control={form.control}
              name={`stages.${index}.title`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Stage Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter stage name" className="bg-secondary" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <FormField
              control={form.control}
              name={`stages.${index}.type`}
              render={({ field }) => (
                <FormItem className="sm:w-3/4 w-full">
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(TurnType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`stages.${index}.duration`}
              render={({ field }) => (
                <FormItem className="sm:w-1/4 w-full">
                  <FormLabel>Duration (min)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      min={1}
                      placeholder="Duration"
                      className="bg-secondary"
                      onKeyPress={(e) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          e.key !== 'Backspace' &&
                          e.key !== 'ArrowLeft' &&
                          e.key !== 'ArrowRight' &&
                          e.key !== 'Delete'
                        ) {
                          e.preventDefault()
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

export const SortableStageItem = ({ id, form, field, index, remove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} key={id} className="w-full">
      <CreateStage form={form} field={field} index={index} remove={remove} />
    </div>
  )
}
